import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatButton } from '@angular/material/button'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { MatInputModule } from '@angular/material/input'
import { MatCard, MatCardHeader, MatCardTitle } from '@angular/material/card'
import { MatIcon } from '@angular/material/icon'
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog'
import { MatOption } from '@angular/material/core'
import { MatSelect } from '@angular/material/select'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { CommonModule } from '@angular/common'
import { map, Observable, startWith } from 'rxjs'
import { NgxMaskDirective } from 'ngx-mask'
import { ViaCepService } from '../../../core/services/address/via-cep/via-cep.service'
import { LocationService } from '../../../core/services/location/location/location.service'
import { removeAccents } from '../../utils/utils'
import { ModalGoogleMapsComponent } from '../../../features/address/modal-google-maps/modal-google-maps.component'
import { ParadaItinerario } from '../../../core/models/parada-itinerario.model'

interface LocationModel {
  id: number
  descricao: string
  cidade: string
  estado: string
}

export interface ParadaFormData {
  parada?: ParadaItinerario
  edit?: boolean
  cityStateOnly?: boolean
  location?: {
    cityOrigin?: string
    stateOrigin?: string
    cityDestination?: string
    stateDestination?: string
  }
}

@Component({
  selector: 'parada-itinerario-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButton,
    MatProgressSpinner,
    MatInputModule,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatIcon,
    MatOption,
    MatSelect,
    MatAutocompleteModule,
    NgxMaskDirective,
  ],
  providers: [ViaCepService, LocationService],
  templateUrl: './parada-itinerario-form.component.html',
  styleUrl: './parada-itinerario-form.component.scss',
})
export class ParadaItinerarioFormComponent implements OnInit {
  constructor(
    private formBuilder: FormBuilder,
    private viaCepService: ViaCepService,
    private locationService: LocationService,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<ParadaItinerarioFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ParadaFormData,
  ) {
    this.edit = !!data?.edit
    this.parada = data?.parada ?? null
    this.location = data?.location ?? {}
    this.cityStateOnly = !!data?.cityStateOnly
  }

  @Output() response = new EventEmitter<ParadaItinerario | null>()

  form: FormGroup = new FormGroup({})
  loading = false
  edit = false
  parada: ParadaItinerario | null = null
  location: ParadaFormData['location'] = {}
  cityStateOnly = false

  listLocation: LocationModel[] = []
  filteredLocations!: Observable<LocationModel[]>
  displayLocation = (loc: LocationModel | null): string =>
    loc ? `${loc.cidade} - ${loc.estado}` : ''

  listArea: any[] = [
    { value: 'S', description: 'Sim' },
    { value: 'N', description: 'Não' },
  ]

  ngOnInit(): void {
    this.createForm()
    if (this.cityStateOnly) {
      this.initLocationAutocomplete()
      this.getLocation()
    }
    if (this.edit && this.parada) {
      this.fillForm(this.parada)
    }
  }

  private getLocation(): void {
    this.locationService.listLocation().subscribe({
      next: (response: any) => {
        this.listLocation = response?.data ?? []
      },
      error: (err) => console.error(err),
    })
  }

  private initLocationAutocomplete(): void {
    this.filteredLocations = this.form.get('locationSelect')!.valueChanges.pipe(
      startWith(''),
      map((v: any) => {
        if (typeof v === 'string') return v
        if (v && typeof v.cidade === 'string' && typeof v.estado === 'string')
          return `${v.cidade} - ${v.estado}`
        return ''
      }),
      map((text: string) => removeAccents((text ?? '').toLowerCase())),
      map((searchText: string) =>
        this.listLocation.filter((loc) => {
          const data = `${loc?.descricao ?? ''} ${loc?.cidade ?? ''} ${loc?.estado ?? ''}`
          return removeAccents(data.toLowerCase()).includes(searchText)
        }),
      ),
    )
  }

  onLocationSelected(event: any): void {
    const loc: LocationModel = event?.option?.value
    if (!loc || typeof loc !== 'object') return
    this.form.get('city')?.setValue(loc.cidade ?? '')
    this.form.get('state')?.setValue(loc.estado ?? '')
  }

  createForm(): void {
    this.form = this.formBuilder.group({
      locationSelect: [''],
      description: ['', this.cityStateOnly ? [] : [Validators.required]],
      postalCode: [''],
      street: [''],
      neighborhood: [''],
      number: [''],
      complement: [''],
      state: ['', [Validators.required]],
      city: ['', [Validators.required]],
      latitude: [''],
      longitude: [''],
      tariffArea: ['N'],
      horaParada: [''],
      tempoPermanencia: [''],
      atividades: [''],
    })
    this.form.get('tariffArea')?.disable()
    this.form.get('state')?.valueChanges.subscribe(() => this.checkCityState())
    this.form.get('city')?.valueChanges.subscribe(() => this.checkCityState())
  }

  fillForm(p: ParadaItinerario): void {
    this.form.get('description')?.setValue(p.localParada ?? '')
    this.form.get('postalCode')?.setValue(p.cep ?? '')
    this.form.get('street')?.setValue(p.logradouro ?? '')
    this.form.get('number')?.setValue(p.numero ?? '')
    this.form.get('complement')?.setValue(p.complemento ?? '')
    this.form.get('neighborhood')?.setValue(p.bairro ?? '')
    this.form.get('city')?.setValue(p.cidade ?? '')
    this.form.get('state')?.setValue(p.estado ?? '')
    if (this.cityStateOnly && (p.cidade || p.estado)) {
      this.form
        .get('locationSelect')
        ?.setValue({ cidade: p.cidade ?? '', estado: p.estado ?? '' })
    }
    this.form.get('latitude')?.setValue(p.latitude ?? '')
    this.form.get('longitude')?.setValue(p.longitude ?? '')
    this.form.get('tariffArea')?.setValue(p.areaTarifario ?? 'N')
    this.form.get('horaParada')?.setValue(p.horaParada ?? '')
    this.form.get('tempoPermanencia')?.setValue(p.tempoPermanencia ?? '')
    this.form.get('atividades')?.setValue(p.atividades ?? '')
  }

  getAddressByPostalCode(): void {
    const postalCode = this.form.get('postalCode')?.value
    if (postalCode && postalCode.length >= 8) {
      this.viaCepService.getAdressbyPostalCode(postalCode).subscribe({
        next: (response: any) => this.fillFromViaCep(response),
        error: (err) => console.error(err),
      })
    }
  }

  fillFromViaCep(element: any): void {
    if (element.erro) {
      this.form.patchValue({
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
      })
    } else {
      this.form.patchValue({
        street: element.logradouro,
        number: '',
        complement: element.complemento,
        neighborhood: element.bairro,
        city: element.localidade,
        state: element.estado ?? element.uf,
      })
    }
  }

  checkCityState(): void {
    const loc = this.location ?? {}
    const city = this.form.get('city')?.value
    const state = this.form.get('state')?.value
    if (
      (loc.cityOrigin === city || loc.cityDestination === city) &&
      (loc.stateOrigin === state || loc.stateDestination === state)
    ) {
      this.form.get('tariffArea')?.setValue('S')
    } else {
      this.form.get('tariffArea')?.setValue('N')
    }
  }

  googleSearch(): void {
    const dialogRef = this.dialog.open(ModalGoogleMapsComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      maxWidth: '900px',
    })
    dialogRef.componentInstance.response.subscribe((response: any) => {
      if (response?.insert) {
        this.fillAddress(response.model)
      }
    })
  }

  fillAddress(model: any): void {
    this.form.patchValue({
      postalCode: model.cep,
      street: model.logradouro,
      number: model.numero,
      complement: model.complemento,
      neighborhood: model.bairro,
      city: model.cidade,
      state: model.estado,
      longitude: model.longitude,
      latitude: model.latitude,
    })
    this.checkCityState()
  }

  checkForm(): boolean {
    return this.form.valid
  }

  buildParada(): ParadaItinerario {
    const f = this.form.controls
    return {
      id: this.parada?.id,
      ordem: this.parada?.ordem ?? 0,
      trecho: this.parada?.trecho ?? 'P',
      areaTarifario: this.form.get('tariffArea')?.value ?? 'N',
      localParada: f['description'].value ?? '',
      cep: f['postalCode'].value ?? '',
      logradouro: f['street'].value ?? '',
      numero: f['number'].value ?? '',
      bairro: f['neighborhood'].value ?? '',
      complemento: f['complement'].value ?? '',
      cidade: f['city'].value ?? '',
      estado: f['state'].value ?? '',
      latitude: f['latitude'].value !== '' ? f['latitude'].value : null,
      longitude: f['longitude'].value !== '' ? f['longitude'].value : null,
      horaParada: f['horaParada'].value ?? '',
      tempoPermanencia: f['tempoPermanencia'].value ?? '',
      atividades: f['atividades'].value ?? '',
    }
  }

  save(): void {
    if (!this.checkForm()) return
    this.response.emit(this.buildParada())
    this.dialogRef.close(this.buildParada())
  }

  close(): void {
    this.response.emit(null)
    this.dialogRef.close(null)
  }
}
